import { forwardRef } from 'react';

import { ButtonLink, Card, ListLayout, LoadingButton, Tooltip } from '@ingrid/luterkort-react';

import { ClearConfirmationModal } from '~src/components/ClearConfirmationModal/ClearConfirmationModal';
import { ConditionalWrapper } from '~src/components/ConditionalWrapper/ConditionalWrapper';
import { useTranslation } from '~src/i18n/use-translation';
import { useCarrierContractsListQuery } from '~src/pages/settings/carrier-contract/queries';

import { useShipmentFormContext } from '../types';
import { CarrierContractSelect, CarrierContractSelectProps } from './carrier-contract-select';

const SelectTrigger = forwardRef<HTMLButtonElement>(function SelectTrigger(props, ref) {
  const { watch } = useShipmentFormContext();
  const { t } = useTranslation();

  const [shipping_method] = watch(['shipping_method']);

  const { data: contracts = [], isLoading } = useCarrierContractsListQuery(
    { shipping_method: shipping_method?.id },
    !!shipping_method
  );

  const enabled = !!shipping_method && contracts.length > 0;

  return (
    <ConditionalWrapper
      when={!enabled}
      wrapper={c => <Tooltip content={t('shipments.carrierContract.selectCarrierContractDisabled')}>{c}</Tooltip>}
    >
      <LoadingButton
        loading={shipping_method && isLoading}
        variant="secondary"
        disabled={!enabled}
        ref={ref}
        {...props}
      >
        {t('common.selectCarrierContract')}
      </LoadingButton>
    </ConditionalWrapper>
  );
});

export function CarrierContract() {
  const { t } = useTranslation();
  const { watch, setValue } = useShipmentFormContext();

  const [carrier_contract, shipping_method] = watch(['carrier_contract', 'shipping_method']);

  const selectProps: Omit<CarrierContractSelectProps, 'trigger'> = {
    shipping_method: shipping_method?.id,
    mode: 'add',
    onValueChange: v => setValue('carrier_contract', v, { shouldDirty: true }),
    value: carrier_contract,
  };

  return (
    <Card.Wrapper as="section">
      <Card.Header>{t('common.carrierContract')}</Card.Header>
      <Card.Content>
        {carrier_contract && (
          <ListLayout.Row>
            <ListLayout.Stack>
              <ListLayout.PrimaryText
                css={{
                  lineHeight: '16px',
                }}
              >
                {carrier_contract.name}
              </ListLayout.PrimaryText>
              <ListLayout.SubRow gap="lg">
                <ListLayout.SecondaryText>
                  {/* Refactor and put in carrier contract package (same for select modal) */}
                  {carrier_contract.carrier_configuration_data
                    ? JSON.parse(carrier_contract.carrier_configuration_data).customer_number
                    : ''}
                </ListLayout.SecondaryText>
                <ListLayout.SecondaryText>{carrier_contract.description}</ListLayout.SecondaryText>
              </ListLayout.SubRow>
            </ListLayout.Stack>
            <ListLayout.Actions>
              <CarrierContractSelect
                {...selectProps}
                mode="update"
                trigger={<ButtonLink>{t('common.edit')}</ButtonLink>}
              />
              <ClearConfirmationModal
                onSubmit={() => setValue('carrier_contract', undefined)}
                clearedItemName={'common.carrierContract'}
              />
            </ListLayout.Actions>
          </ListLayout.Row>
        )}
        {!carrier_contract && <CarrierContractSelect {...selectProps} trigger={<SelectTrigger />} />}
      </Card.Content>
    </Card.Wrapper>
  );
}
